/**
 * Ghost rolls (game option, on by default): a player out of cards gets one roll of all five
 * dice at the end of each later round, once everyone's turns are over. Matching the Kill Quest
 * of any card on the table brings one of their lost cards back to their hand.
 *
 * - `GhostPanel`: the middle of the table during a ghost turn: who rolls, the dice (rolling one
 *   at a time, like any roll), the Ghost Roll button for the ghost, then the outcome.
 * - `useGhostReveal`: the server moves on as soon as the dice are rolled (the next ghost, or the
 *   round ends), so the table is held as it was, with the ghost's dice, until they have landed
 *   and the outcome has shown for `GHOST_REVEAL_MS` (`busy` meanwhile). Rolls before mount aren't replayed.
 * - `useGhostLesson` / `GhostLesson`: Beginner Mode, the moment you run out of cards: what a ghost is.
 */
import { useEffect, useState } from 'react';
import { DICE_COUNT, GHOST_REVEAL_MS, t, type GameView, type PlayerId } from '@kadice/shared';
import { afterRoll, rollMs, rollingDice, type RollFx } from '../rolling';
import { useMotionArm } from '../dice3d/DiceTray';
import { anim } from '../speed';
import { tx } from '../text';
import type { GameClient } from '../useGameClient';
import { Dice } from './Dice';
import { PName } from './Names';

export function useGhostReveal(view: GameView): { view: GameView; busy: boolean } {
  const seq = view.lastGhost?.seq ?? 0;
  // `roll`: the ghost roll and its time on this screen (the 3D tray can hold it open longer).
  const [reveal, setReveal] = useState<{
    seq: number;
    last: GameView;
    frozen: GameView | null;
    ms: number;
    roll: { seq: number | null; ms: number };
  }>({
    seq,
    last: view,
    frozen: null,
    ms: 0,
    roll: { seq: null, ms: 0 },
  });

  let current = reveal;
  if (seq !== reveal.seq) {
    const before = reveal.last;
    const ghost = view.lastGhost;
    const turn = before.turn;
    const show =
      !!ghost &&
      seq > reveal.seq &&
      before.gameId === view.gameId &&
      before.phase === 'playing' &&
      turn?.ghost === true &&
      turn.playerId === ghost.playerId;
    const frozen = show
      ? { ...before, lastGhost: ghost, turn: { ...turn, pool: ghost.dice, rolled: ghost.dice.length, hasRolled: true } }
      : null;
    current = {
      seq,
      last: view,
      frozen,
      ms: anim(GHOST_REVEAL_MS),
      roll: { seq: view.lastRoll?.seq ?? null, ms: rollMs(view.lastRoll) },
    };
    setReveal(current);
  } else if (reveal.last !== view) {
    current = { ...reveal, last: view };
    setReveal(current);
  }

  const { frozen, ms, roll } = current;
  useEffect(() => {
    if (!frozen) return;
    return afterRoll(roll.seq, roll.ms, ms, () => setReveal((r) => (r.frozen === frozen ? { ...r, frozen: null } : r)));
  }, [frozen, ms, roll]);

  return { view: frozen ?? view, busy: frozen !== null };
}

/** The middle of the table during a ghost turn. */
export function GhostPanel({ client, view, rollFx }: { client: GameClient; view: GameView; rollFx: RollFx | null }) {
  const turn = view.turn;
  const arm = useMotionArm();
  if (!turn?.ghost) return null;
  const me = view.you.id;
  const name = (id: PlayerId | null) => view.players.find((p) => p.id === id)?.name ?? t('player.someone');
  const mine = turn.playerId === me;
  const fx = rollFx?.playerId === turn.playerId && !rollFx.war ? rollFx : null;
  const shown = rollingDice(turn.pool, 0, fx);
  const result = turn.hasRolled && view.lastGhost?.playerId === turn.playerId ? view.lastGhost : null;
  const landed = !!result && shown.rolling.length === 0;
  const ghostName = <PName>{name(turn.playerId)}</PName>;
  const victim = result && <PName>{name(result.matchedId)}</PName>;

  return (
    <div className="ghost-panel" role="status">
      <h3>{mine ? t('ghost.yours') : tx('ghost.rolls', { name: ghostName })}</h3>
      <p className="muted small">{t('ghost.how')}</p>
      {/* Dice land in order, one every BOT_DIE_STAGGER_MS; the places still to fill are dashed
          outlines, as on a turn. */}
      <Dice
        values={turn.hasRolled ? shown.values.filter((v) => v !== 0) : []}
        rolling={shown.rolling}
        slots={DICE_COUNT}
      />
      {mine && !turn.hasRolled && (
        <button
          className="primary"
          disabled={view.waitingFor.length > 0}
          onClick={() =>
            arm
              ? arm({
                  count: DICE_COUNT,
                  quick: true,
                  send: async (toss) => (await client.request('game:ghostRoll', { toss })) !== undefined,
                })
              : void client.request('game:ghostRoll', {})
          }
        >
          {t('ghost.roll')}
        </button>
      )}
      {landed && result && (
        <p className={result.matchedId === null ? 'ghost-miss' : 'ghost-hit'}>
          {result.matchedId === null
            ? mine
              ? t('ghost.missYou')
              : tx('ghost.miss', { name: ghostName })
            : mine
              ? tx('ghost.hitYou', { victim })
              : tx('ghost.hit', { name: ghostName, victim })}
        </p>
      )}
    </div>
  );
}

/**
 * Beginner Mode: true from the moment you run out of cards (ghost rolls on) until `done`. Fed
 * the shown table, so it comes after the attack that put you out has played.
 */
export function useGhostLesson(view: GameView, enabled: boolean): { show: boolean; done: () => void } {
  const self = view.you.spectating ? undefined : view.players.find((p) => p.id === view.you.id);
  const out = !!self && self.status === 'out';
  const [state, setState] = useState({ out, pending: false });
  if (out !== state.out) setState({ out, pending: out && enabled && view.settings.ghostRolls });
  return { show: state.pending && enabled, done: () => setState((s) => ({ ...s, pending: false })) };
}

/**
 * `ended`: the game ended in the same turn you went out, so you never get your roll; `winner`
 * names who won (null: no survivors).
 */
export function GhostLesson({ onDone, ended, winner }: { onDone: () => void; ended: boolean; winner: string | null }) {
  return (
    <div className="modal-backdrop">
      <div className="modal splash ghost-lesson" role="dialog" aria-modal="true">
        <h2>{t('ghostLesson.title')}</h2>
        <p>{t('ghostLesson.body')}</p>
        <p className="muted">
          {!ended
            ? t('ghostLesson.rules')
            : winner !== null
              ? tx('ghostLesson.ended', { name: <PName>{winner}</PName> })
              : t('ghostLesson.endedNoWinner')}
        </p>
        <div className="row actions">
          <button className="primary intro-next" onClick={onDone}>
            {t('common.next')}
          </button>
        </div>
      </div>
    </div>
  );
}
